import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["Transmission", "Substations & O&M", "Renewable Energy / Wind O&M", "Civil & Infrastructure", "Other"];

const inputCls =
    "w-full border border-vec-border bg-white px-4 py-3 text-sm text-vec-charcoal placeholder:text-vec-charcoal/35 focus:border-vec-green focus:outline-none transition-colors duration-300";

export const Careers = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", experience: "", message: "" });
    const [resume, setResume] = useState(null);
    const [sending, setSending] = useState(false);
    const fileRef = useRef(null);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            const data = new FormData();
            Object.entries(form).forEach(([k, v]) => v && data.append(k, v));
            if (resume) data.append("resume", resume);
            await axios.post(`${API}/applications`, data);
            toast.success("Application received. Thank you — our team will review your profile.");
            setForm({ name: "", email: "", phone: "", interest: "", experience: "", message: "" });
            setResume(null);
            if (fileRef.current) fileRef.current.value = "";
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not submit your application. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="careers" data-testid="careers-section" className="relative bg-vec-mist py-16 lg:py-32 border-t border-vec-border tech-grid">
            <div className="vec-container">
                <div className="grid grid-cols-12 gap-y-10 lg:gap-x-14">
                    <div className="col-span-12 lg:col-span-5">
                        <SectionHead
                            index="VEC"
                            eyebrow="Careers"
                            title="Build Your Career with VEC"
                            sub="Work with teams delivering critical power infrastructure across diverse project environments."
                        />
                        <Reveal delay={0.2}>
                            <div className="mt-8 border border-vec-border bg-white p-6">
                                <p className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-vec-green">
                                    Open Positions
                                </p>
                                <p className="mt-3 text-sm text-vec-charcoal/60" data-testid="careers-openings-placeholder">
                                    Current openings will be listed here.
                                </p>
                                <p className="mt-4 text-sm text-vec-charcoal/70">
                                    Until then, share your profile — we keep promising candidates in
                                    mind for upcoming project requirements.
                                </p>
                            </div>
                        </Reveal>
                    </div>

                    <div className="col-span-12 lg:col-span-7">
                        <Reveal delay={0.1}>
                            <form
                                data-testid="careers-application-form"
                                onSubmit={submit}
                                className="border border-vec-border bg-white p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5"
                            >
                                <div>
                                    <label htmlFor="career-name" className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-vec-charcoal/55 mb-2">Full Name *</label>
                                    <input id="career-name" data-testid="careers-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                                </div>
                                <div>
                                    <label htmlFor="career-email" className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-vec-charcoal/55 mb-2">Email *</label>
                                    <input id="career-email" data-testid="careers-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
                                </div>
                                <div>
                                    <label htmlFor="career-phone" className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-vec-charcoal/55 mb-2">Phone</label>
                                    <input id="career-phone" data-testid="careers-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91" />
                                </div>
                                <div>
                                    <label htmlFor="career-interest" className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-vec-charcoal/55 mb-2">Area of Interest</label>
                                    <select id="career-interest" data-testid="careers-interest-select" value={form.interest} onChange={set("interest")} className={inputCls}>
                                        <option value="">Select an area</option>
                                        {INTERESTS.map((o) => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="career-experience" className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-vec-charcoal/55 mb-2">Experience</label>
                                    <input id="career-experience" data-testid="careers-experience-input" value={form.experience} onChange={set("experience")} className={inputCls} placeholder="e.g. 5 years — substation maintenance" />
                                </div>
                                <div>
                                    <label htmlFor="career-resume" className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-vec-charcoal/55 mb-2">Resume (PDF / DOC, max 5 MB)</label>
                                    <label
                                        htmlFor="career-resume"
                                        data-testid="careers-resume-upload"
                                        className="flex cursor-pointer items-center gap-3 border border-dashed border-vec-border hover:border-vec-green px-4 py-3 text-sm text-vec-charcoal/60 transition-colors duration-300"
                                    >
                                        <Upload size={15} className="text-vec-green shrink-0" aria-hidden="true" />
                                        <span className="truncate">{resume ? resume.name : "Choose file"}</span>
                                    </label>
                                    <input
                                        id="career-resume"
                                        ref={fileRef}
                                        type="file"
                                        accept=".pdf,.doc,.docx"
                                        className="sr-only"
                                        onChange={(e) => setResume(e.target.files?.[0] || null)}
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="career-message" className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-vec-charcoal/55 mb-2">Message</label>
                                    <textarea id="career-message" data-testid="careers-message-input" rows={3} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us briefly about yourself" />
                                </div>
                                <div className="sm:col-span-2">
                                    <button
                                        type="submit"
                                        data-testid="careers-submit-button"
                                        disabled={sending}
                                        className="group inline-flex items-center gap-3 bg-vec-green hover:bg-vec-green-bright disabled:opacity-60 text-white font-semibold text-sm tracking-wide px-7 py-4 transition-colors duration-300"
                                    >
                                        {sending ? "Submitting…" : "Submit Application"}
                                        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                                    </button>
                                </div>
                            </form>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
